import { CONVERSION_TABLE } from '@constants/conversion';

export function roundPrecision(
  value: number,
  precision: number,
  trimZeros = false
): string {
  const precisionConst = 10 ** precision;
  const rounded = Math.round(value * precisionConst) / precisionConst;
  if (trimZeros) {
    return parseFloat(rounded.toFixed(precision)).toString();
  }
  return rounded.toFixed(precision);
}

export function stylizeUnit(unit: string): string {
  let formatted = unit;
  formatted = formatted.replace('^12', '¹²');
  formatted = formatted.replace('^10', '¹⁰');
  formatted = formatted.replace('^9', '⁹');
  formatted = formatted.replace('^3', '³');
  formatted = formatted.replace('No Unit', '');
  formatted = formatted.replace('Celsius', '°C');
  formatted = formatted.replace('Fahrenheit', '°F');
  return formatted;
}

export function convertUnit(
  value?: number[] | boolean | number | string | null,
  unit?: string | null,
  system?: string,
  precision = 2,
  table = 'default',
  multiplier = 1,
  trimZeros = false
): string {
  if (typeof value === 'boolean') {
    return value ? 'Positive' : 'Negative';
  }
  if (value instanceof Array) {
    return value
      .map((v) => convertUnit(v * multiplier, unit, system, precision, table))
      .join('/');
  }
  if (typeof value !== 'number' || !(table in CONVERSION_TABLE) || !unit) {
    return String(
      typeof value === 'number'
        ? roundPrecision(value * multiplier, precision, trimZeros)
        : value
    );
  }
  // Correct unit, just round
  const [siUnit, conUnit, siToCon, conToSi] = CONVERSION_TABLE[table];
  if (
    (system === 'SI' &&
      (siUnit instanceof Array ? siUnit.includes(unit) : siUnit === unit)) ||
    (system === 'CON' &&
      (conUnit instanceof Array ? conUnit.includes(unit) : conUnit === unit))
  ) {
    return roundPrecision(value * multiplier, precision, trimZeros);
  }

  // Needs conversion
  if (system === 'SI') {
    return roundPrecision(conToSi(value * multiplier), precision, trimZeros);
  }

  if (system === 'CON') {
    return roundPrecision(siToCon(value * multiplier), precision, trimZeros);
  }

  return String(roundPrecision(value * multiplier, precision, trimZeros));
}

export function getUnit(
  unit: string | null | undefined,
  system: string,
  table = 'default'
): string {
  if (!unit) {
    return '';
  }

  if (table in CONVERSION_TABLE) {
    const [siUnit, conUnit] = CONVERSION_TABLE[table];

    if (system === 'SI') {
      return stylizeUnit(siUnit instanceof Array ? siUnit[0] : siUnit);
    } else if (system === 'CON') {
      return stylizeUnit(conUnit instanceof Array ? conUnit[0] : conUnit);
    }
  }

  return stylizeUnit(unit);
}

export function getUnits(table = 'default'): { SI: string; CON: string } {
  if (table in CONVERSION_TABLE) {
    const [siUnit, conUnit] = CONVERSION_TABLE[table];
    return {
      SI: stylizeUnit(siUnit instanceof Array ? siUnit[0] : siUnit),
      CON: stylizeUnit(conUnit instanceof Array ? conUnit[0] : conUnit),
    };
  }
  return { SI: '', CON: '' };
}
